import { serverBaseUrl } from "../constants/Constants";
import { getCookieToken } from "../utils/utils";
import { appStore } from "../store/Store";
import {
  assignCacheTabId,
  clearAllPersistedApiCache,
  clearPersistedApiCacheByTabId,
  getHydratedApiCacheObject,
  persistApiCacheEntry,
  readPersistedApiCacheEntry,
} from "../utils/apiCacheStorage";

/**
 * Decide whether a GET must skip the on-device cache and always hit the network.
 *
 * Only the async AI features are bypassed: their GETs return *live server-side
 * state* (job/session status that flips from "processing" to "ready" on its own,
 * AI generation), so a cached copy would freeze the UI on stale data.
 *
 * Everything else — the CRUD data tabs (categories, notes, todos, projects,
 * portfolios, files, urls, org-tree, splitwise, calendar, …) — is cached on the
 * device. Your own mutations clear the whole cache (see the POST/PUT/DELETE path
 * below), and each tab's "Clear page cache" button (TabCacheButton →
 * clearApiCacheForTab) flushes that tab on demand. The one thing the cache can't
 * auto-detect is another user revoking/sharing a collaborative folder — the
 * per-tab clear button is the manual escape hatch for that.
 */
function shouldBypassApiCacheForGet(endpoint) {
  const e = String(endpoint || "");
  if (e.startsWith("dashboard/screen-listen")) return true;
  if (e.startsWith("dashboard/media-analyze")) return true;
  if (e.startsWith("dashboard/voice-analyse")) return true;
  if (e.startsWith("dashboard/video-ai")) return true;
  if (e.startsWith("transfer-ai")) return true;
  if (e.startsWith("dashboard/exam-ai")) return true;
  return false;
}

/** Clears in-memory GET cache (Redux) and persisted browser cache. */
export function clearClientApiCache() {
  clearAllPersistedApiCache();
  appStore.dispatch({ type: "CLEAR_API_CACHE" });
}

/** Merge GET cache from localStorage into Redux (call once on app load). */
export function hydrateApiCacheFromLocalStorage() {
  const flat = getHydratedApiCacheObject();
  const skipPrefixes = [
    "GET:categories",
    "GET:todos?",
    "GET:projects?",
    "GET:portfolios/",
    "GET:notes",
    "GET:files",
    "GET:org-tree",
    "GET:org-tree/my-assignments",
  ];
  const filtered = {};
  for (const [k, v] of Object.entries(flat)) {
    if (skipPrefixes.some((p) => k.startsWith(p))) continue;
    filtered[k] = v;
  }
  if (Object.keys(filtered).length > 0) {
    appStore.dispatch({ type: "HYDRATE_API_CACHE", value: filtered });
  }
}

/**
 * Clears cached GET responses for one dashboard area (in-memory + localStorage) so
 * the next request hits the network. Returns how many request keys were removed.
 * @param {string} tabId e.g. "notes" | "project" — see route segment under /dashboard/…
 */
export function clearApiCacheForTab(tabId) {
  const removed = clearPersistedApiCacheByTabId(tabId);
  if (removed.length) {
    appStore.dispatch({ type: "REMOVE_API_CACHE_KEYS", keys: removed });
  }
  return removed.length;
}

/** True once profile sync has loaded userTokenDetails (may still be zero credits). */
export function areAiCreditsKnown() {
  return Boolean(appStore.getState()?.user?.userTokenDetails);
}

/** Remaining AI Usage Credits (limit minus used) from Profile balance. */
export function getRemainingAiCredits() {
  const user = appStore.getState()?.user || {};
  const token = user?.userTokenDetails || null;
  if (!token) return 0;
  const total = Number(token.tokenLimit || 0);
  const used = Number(token.totalTokensUsed || 0);
  return Math.max(0, total - used);
}

/** @deprecated use getRemainingAiCredits */
export const getRemainingVoiceCredits = getRemainingAiCredits;

export function hasAiCredits() {
  return getRemainingAiCredits() > 0;
}

/** @deprecated use hasAiCredits */
export const hasVoiceCreditsForScreenAi = hasAiCredits;

function notifyAiCreditsExhausted() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("manage360:ai-credits-exhausted"));
    window.dispatchEvent(new CustomEvent("manage360:voice-credits-exhausted"));
  }
}

export async function hitBeApi(endpoint, method = "GET", body = null) {
  try {
    const normalizedMethod = method.toUpperCase();
    const isVoiceAssistant =
      typeof endpoint === "string" &&
      (endpoint.includes("voice-assistant") ||
        (endpoint.includes("screen-listen") &&
          !endpoint.includes("/sessions") &&
          endpoint !== "dashboard/screen-listen/sessions") ||
        (endpoint.includes("media-analyze") &&
          !endpoint.includes("/jobs")) ||
        (endpoint.includes("voice-analyse") &&
          endpoint.endsWith("/analyze")) ||
        (endpoint.includes("video-ai") &&
          endpoint.endsWith("/analyze")) ||
        (endpoint.includes("exam-ai/exams") &&
          !endpoint.includes("exam-ai/exams/")) ||
        (endpoint.includes("exam-ai/chats/") &&
          endpoint.endsWith("/messages")) ||
        endpoint.endsWith("current-affairs/day") ||
        endpoint.endsWith("current-affairs/quiz") ||
        endpoint.endsWith("/generate-subtopics"));

    if (normalizedMethod === "POST" && isVoiceAssistant && areAiCreditsKnown()) {
      const remaining = getRemainingAiCredits();
      if (remaining <= 0) {
        notifyAiCreditsExhausted();
        throw new Error(
          "AI Usage Credits exhausted. Buy a pack from Profile or Pricing to use Screen AI, Media AI, and other AI features."
        );
      }
    }

    const requestKey = `${normalizedMethod}:${endpoint}`;
    const state = appStore.getState();

    const bypassGetCache = shouldBypassApiCacheForGet(endpoint);

    if (normalizedMethod === "GET" && !bypassGetCache) {
      const cachedResponse = state?.apiCache?.[requestKey];
      if (cachedResponse) {
        return cachedResponse;
      }
      const persisted = readPersistedApiCacheEntry(requestKey);
      if (persisted) {
        const data = persisted.data;
        appStore.dispatch({
          type: "SET_API_CACHE",
          key: requestKey,
          value: data,
        });
        return data;
      }
    }

    const token = getCookieToken();
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
    const response = await fetch(`${serverBaseUrl}/${endpoint}`, {
      method: normalizedMethod,
      headers: headers,
      body: body ? JSON.stringify(body) : null,
      cache:
        normalizedMethod === "GET" || isVoiceAssistant ? "no-store" : "default",
    });

    if (!response.ok) {
      let errorResponse = null;
      try {
        errorResponse = await response.json();
      } catch {
        throw new Error(
          `Request failed (${response.status} ${response.statusText || ""})`.trim()
        );
      }
      const rawMsg = errorResponse?.message ?? errorResponse?.error;
      const text =
        typeof rawMsg === "string"
          ? rawMsg
          : rawMsg != null
            ? JSON.stringify(rawMsg)
            : `Request failed (${response.status})`;
      if (
        response.status === 402 ||
        /credit|token limit|tokens exhausted/i.test(String(text))
      ) {
        notifyAiCreditsExhausted();
      }
      /** Allow multi-line Razorpay setup hints (~300 chars); keep other errors bounded */
      const maxLen =
        typeof text === "string" && /razorpay|RAZORPAY/i.test(text) ? 500 : 200;
      throw new Error(String(text).slice(0, maxLen));
    }

    const data = await response.json();

    if (normalizedMethod === "GET") {
      if (!bypassGetCache) {
        appStore.dispatch({
          type: "SET_API_CACHE",
          key: requestKey,
          value: data,
        });
        const tabId = assignCacheTabId(endpoint, "GET");
        persistApiCacheEntry(requestKey, data, tabId);
      }
    } else {
      // Mutations can change multiple resources, so invalidate cache globally.
      clearAllPersistedApiCache();
      appStore.dispatch({
        type: "CLEAR_API_CACHE",
      });
    }

    return data;
  } catch (error) {
    const msg = String(error?.message || "").toLowerCase();
    if (
      msg.includes("credit") ||
      msg.includes("token limit") ||
      msg.includes("tokens exhausted")
    ) {
      notifyAiCreditsExhausted();
    }
    throw error;
  }
}

/** Multipart POST (e.g. screen audio upload). Uses AI Usage Credits gate when applicable. */
export async function hitBeApiFormData(endpoint, formData) {
  try {
  const usesAiCredits =
    typeof endpoint === "string" &&
    ((endpoint.includes("screen-listen") && !endpoint.includes("/sessions")) ||
      (endpoint.includes("media-analyze") && !endpoint.includes("/jobs")));

  if (usesAiCredits && areAiCreditsKnown()) {
    const remaining = getRemainingAiCredits();
    if (remaining <= 0) {
      notifyAiCreditsExhausted();
      throw new Error(
        "AI Usage Credits exhausted. Buy a pack from Profile or Pricing to use Screen AI or Media AI."
      );
    }
  }

  const token = getCookieToken();
  const response = await fetch(`${serverBaseUrl}/${endpoint}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
    cache: "no-store",
  });

  if (!response.ok) {
    let errorResponse = null;
    try {
      errorResponse = await response.json();
    } catch {
      throw new Error(
        `Request failed (${response.status} ${response.statusText || ""})`.trim()
      );
    }
    const rawMsg = errorResponse?.message ?? errorResponse?.error;
    const text =
      typeof rawMsg === "string"
        ? rawMsg
        : rawMsg != null
          ? JSON.stringify(rawMsg)
          : `Request failed (${response.status})`;
    if (
      response.status === 402 ||
      /credit|token limit|tokens exhausted/i.test(String(text))
    ) {
      notifyAiCreditsExhausted();
    }
    throw new Error(String(text).slice(0, 400));
  }

  const data = await response.json();
  clearAllPersistedApiCache();
  appStore.dispatch({ type: "CLEAR_API_CACHE" });
  return data;
} catch (error) {
  const msg = String(error?.message || "").toLowerCase();
  if (
    msg.includes("credit") ||
    msg.includes("token limit") ||
    msg.includes("tokens exhausted")
  ) {
    notifyAiCreditsExhausted();
  }
  throw error;
}
}
